/**
 * 작성자 : 박승호
 * 날짜 : 2025.01.06
 * 경로 : 사내용 상황판 > 출장 현장 기상 정보
 */

import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import SkeletonTable from '../SkeletonTable';
import mapImage from '../../assets/images/common/korea_map_light.png';
import mapImageDark from '../../assets/images/common/korea_map_dark.png';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { bScreenMode } from '../../atoms';
import { getWeatherIcon } from '../../utils/weatherUtils';
import { WEATHER_ERROR_MESSAGE } from '../../_constants';
import IssueGuide from '../IssueGuide';
import useTravelWeather, { TRAVEL_WEATHER_COLORS as COLORS } from '../../hooks/useTravelWeather';

const Root = styled.div`
  display: flex;
  user-select: none;
  height: 100%;
  --test: #1e3a63;

  .weatherError {
    position: absolute;
    height: auto;
    width: 100%;
    > div {
      width: inherit;
      > p {
        display: flex;
        align-items: center;
        justify-content: center;
        width: inherit;
        padding: 0.25rem 0;
        color: #fff;
        background-color: rgba(0, 0, 0, 0.4);
        border-radius: 0;
        gap: 0.25rem;

        > span.material-symbols-rounded {
          font-size: 20px;
        }
      }
    }
  }

  .no-weather-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    gap: 0.5rem;
    > span.material-symbols-rounded {
      font-size: 2.5rem;
      margin-bottom: 0.5rem;
    }
  }

  > .widget-body {
    &.card {
      width: 100%;
      height: calc(100% - 3rem);
      flex-grow: 1;
      gap: 0.5rem;
      padding: 1.5rem;
      // padding: 1rem;
      overflow: hidden;

      .weather_cont {
        display: flex;
        justify-content: space-between;
        border-radius: 0.5rem;
        width: 100%;
        height: 100%;
        overflow: hidden;
        gap: 0.5rem;

        > .weather-map-img {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          border-radius: 0.25rem;
          width: 54%;
          padding: 3rem 0;
          // width: calc(57% - 0.25rem);

          > .widget-headers {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            z-index: 10;
            // padding: 0.5rem 0.625rem 1rem 0.625rem;
            display: flex;
            gap: 0.5rem;
            width: 100%;

            &.margin-top {
              margin-bottom: 0;
            }

            > .widget-title {
              > span {
                align-items: center;
                font-size: var(--widget-font-size-lg);
                color: var(--text-primary);
                font-weight: 600;

                > span {
                  font-size: inherit;
                }
              }
            }
          }

          > figure {
            display: block;
            position: relative;
            width: 100%;
            height: 100%;
            // overflow: hidden;

            > img {
              display: block;
              // width: 100%;
              margin: 0 auto;
              height: 100%;
              object-fit: contain;
            }

            .marker-location-badge {
              position: absolute;
              display: flex;
              align-items: center;
              justify-content: center;
              min-width: 3rem;
              width: auto;
              min-height: 1.625rem;
              color: #fff;
              border-radius: 0.25rem;
              font-size: var(--widget-font-size-sm);
              line-height: 1;
              padding: 0.25rem 0.5rem;
            }

            .mark {
              position: absolute;
              display: flex;
              align-items: center;
              justify-content: center;
              width: 16px;
              height: 16px;
              background: var(--widget-status-red);
              border-radius: 16px;
              z-index: 99999;

              span {
                font-size: 12px;
                font-weight: 800;
                color: #fff;
              }
            }
          }
        }
      }

      .weather-wrapper {
        display: grid;
        // grid-template-rows: repeat(3, calc(33.3% - 0.33rem));
        grid-template-rows: repeat(4, calc(25% - 0.4rem));
        gap: 0.5rem;
        width: 46%;
        height: 100%;

        .weather-item {
          display: flex;
          align-items: stretch;
          width: 100%;
          height: 100%;
          flex-direction: row-reverse;
          // background-color: var(--widget-theme-tonal-blue);
          border-radius: 1.5rem;
          // color: #fff;
          color: var(--text-primary);
          overflow: hidden;
          padding: 0.5rem;

          &.active {
            background: var(--widget-sub-bg);

            .weather-left {
              background: var(--widget-primary);
              box-shadow: 2px 2px 6px 0 rgba(60, 141, 247, 0.15);

              .weather-bottom {
                color: #fff;
              }
            }

            .weather-right {
              // color: var(--widget-primary);
              color: var(--text-primary);
              flex-grow: 1;

              .weather-head {
                svg path {
                  fill: var(--text-primary) !important;
                  // fill: var(--widget-primary) !important;
                }

                .weather-address {
                  span {
                    color: var(--text-primary);
                    // color: var(--widget-primary);
                  }
                }

                h4 {
                  span {
                    color: var(--text-primary);
                    // color: var(--widget-primary);
                  }
                }
              }
            }
          }

          .weather-left {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            width: 12rem;
            height: 100%;
            padding: 0.5rem 0;
            border-radius: 1rem;
            background: var(--widget-sub-bg);
            // background: var(--widget-bg);
            // background: var(--widget-theme-tonal-blue);

            .weather-temperature {
              font-size: var(--widget-font-size-3xl);
              font-weight: 700;
              line-height: 1;
            }

            .weather-bottom {
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 0.25rem;
              color: var(--text-primary);

              .weather-name {
                display: none;
                font-size: var(--widget-font-size-lg);
                line-height: 1;
              }
            }
          }

          .weather-right {
            display: flex;
            flex-direction: column;
            justify-content: space-around;
            padding: 0.75rem;
            width: calc(100% - 114px);
            height: 100%;

            .weather-head {
              display: flex;
              flex-direction: column;
              gap: 0.5rem;

              svg path {
                fill: var(--widget-sub-text) !important;
              }

              .weather-address {
                display: flex;
                align-items: center;
                gap: 0.25rem;

                span {
                  color: var(--widget-sub-text);
                  // color: var(--widget-text-secondary);
                  // font-size: var(--widget-font-size-lg);
                  font-size: 20px;
                  font-weight: 500;
                  line-height: 1;
                }
              }

              h4 {
                display: flex;
                align-items: flex-start;
                gap: 0.5rem;

                span {
                  display: -webkit-box;
                  -webkit-line-clamp: 2;
                  -webkit-box-orient: vertical;
                  text-overflow: ellipsis;
                  overflow: hidden;
                  font-size: 32px;
                  // font-size: var(--widget-font-size-2xl);
                  font-weight: 700;
                  line-height: 1.4;
                  // color: var(--widget-text-secondary);
                  color: var(--text-primary);
                  flex: 1;
                }
              }
            }

            .weather-etc-info {
              display: flex;
              gap: 0.875rem;
              font-size: 20px;
              color: var(--text-tertiary);

              > b {
                display: none;
                color: var(--text-tertiary);
                font-size: var(--widget-font-size-sm);
              }

              .low,
              .high {
                display: flex;
                align-items: center;
                gap: 0.25rem;

                > b {
                  font-size: 28px;
                }
              }

              .high {
                b {
                  color: var(--widget-status-red);
                }
              }

              .low {
                b {
                  color: var(--widget-status-blue);
                }
              }
            }
          }

          .weather-info {
            display: flex;
            align-items: center;
            height: calc(100% - 36px);
            gap: 0.5rem;
            padding: 0.25rem 0;

            img {
              width: 44px;
            }

            .weather-dt-info {
              display: flex;
              flex-direction: column;
              // gap: 0.25rem;

              > p {
                font-size: 18px;
                font-weight: 500;

                > span {
                  font-size: 12px;
                }
              }
            }

            .weather-etc-info {
              display: flex;
              gap: 0.5rem;

              .high {
                display: flex;
                align-items: center;
                gap: 0.25rem;
                font-size: var(--widget-font-size-lg);
                color: var(--widget-status-red);

                > span {
                  display: flex;
                  align-items: center;
                }

                b {
                  font-weight: 800;
                }
              }

              .low {
                display: flex;
                align-items: center;
                gap: 0.25rem;
                font-size: var(--widget-font-size-lg);
                color: var(--widget-status-blue);

                > span {
                  display: flex;
                  align-items: center;
                }

                b {
                  font-weight: 800;
                }
              }

              span.material-symbols-rounded {
                font-variation-settings: 'FILL' 1, 'wght' 500;
                font-size: var(--widget-font-size-xl);
                line-height: 1.2;
              }
            }
          }
        }
      }
    }
  }

  > .skeleton-wrap {
    > div {
      padding: 0;

      .skeleton-row {
        height: 100%;
        margin-bottom: 0;

        span {
          display: block;
          height: 100% !important;
        }
      }
    }
  }

  .no-data {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 40%;
    height: 100%;
    background-color: var(--widget-sub-bg);
    border-radius: 1.5rem;
    color: var(--text-primary);
    font-size: var(--widget-font-size-lg);
    font-weight: 400;
    gap: 1.5rem;

    .no-data-content {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 1rem;

      p {
        width: 300px;
        text-align: center;
        line-height: 1.4;
        word-break: keep-all;
      }

      span {
        color: var(--widget-sub-text);
        font-size: 88px;

        &.material-symbols-rounded {
          font-variation-settings: 'FILL' 1, 'wght' 600, 'GRAD' 200, 'opsz' 40;
        }
      }
    }

    .retry-time {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 24px 48px;
      border-radius: 1rem;
      background: var(--widget-bg);

      span {
        font-size: var(--widget-font-size-sm);
        color: var(--widget-sub-text);
      }

      p {
        font-size: var(--widget-font-size-2xl);
        color: var(--widget-primary);
        text-align: center;
        line-height: 1.4;
        word-break: keep-all;
      }
    }
  }
`;

interface IProps {
  name: string;
  userInfo: any;
  refreshTimeCount: number;
}

// const LAT_RANGE: { min: number; max: number } = { min: 33.0, max: 38.8 }; // 제주도 ( 살짝 밑 )에서 강원도 ( 살짝 위 )까지
// const LON_RANGE: { min: number; max: number } = { min: 125.0, max: 131.0 }; // 진도에서 울릉도까지
const LAT_RANGE: { min: number; max: number } = { min: 33.28, max: 38.62 }; // 제주도 ( 살짝 밑 )에서 강원도 ( 살짝 위 )까지 - 이미지 수정
const LON_RANGE: { min: number; max: number } = { min: 125.35, max: 130.0 }; // 진도에서 울릉도까지 - 이미지 수정
const LAT_RANGE_CORRECTION = 0.12; // 위도가 33.57보다 작을 때 해당 위도 + 0.2 값 적용 - 제주도 위치 조정해서 재 계산 필요
const LON_RANGE_CORRECTION = 1.16; // 경도가 130.79 이상일 때 해당 경도 - 1.09 값 적용 - 을릉도 위치 조정해서 재 계산 필요

// 뱃지 위치 계산 함수
const calculateBadgePosition = (xPos: number, imgW: number, imgLeft: number, badgeWidth: number = 120) => {
  let badgeX = xPos;
  let badgeTransform = 'translateX(-50%)';

  const mapPadding = 5;
  const mapRightEdge = imgW + imgLeft - mapPadding;
  const mapLeftEdge = mapPadding;

  // const mapLeftEdge = imgLeft;
  // const mapRightEdge = imgW + imgLeft;

  const badgeRightEdge = xPos + badgeWidth / 2;
  const badgeLeftEdge = xPos - badgeWidth / 2;

  if (badgeRightEdge > mapRightEdge) {
    // 오른쪽 끝에 닿으면 왼쪽으로 이동
    badgeX = mapRightEdge - badgeWidth / -2;
    badgeTransform = 'translateX(0)';
  } else if (badgeLeftEdge < mapLeftEdge) {
    // 왼쪽 끝에 닿으면 오른쪽으로 이동
    badgeX = mapLeftEdge + badgeWidth / 2;
    badgeTransform = 'translateX(0)';
  }

  return { badgeX, badgeTransform };
};

const TravelWeatherInfo = ({ name, userInfo, refreshTimeCount }: IProps) => {
  const isTheme = useRecoilValue(bScreenMode); // 현재 상황판 테마 상태
  const { t } = useTranslation();
  const [totalLength, setTotalLength] = useState<number>(0);
  const [currents, setCurrents] = useState<any>([]);
  const [imgW, setImgW] = useState(0); // 이미지 너비
  const [imgH, setImgH] = useState(0); // 이미지 높이
  const [imgLeft, setImgLeft] = useState(0); // 이미지 왼쪽 여백 위치
  const [activeIndex, setActiveIndex] = useState<number>(1); // 현재 활성화된 아이템 인덱스
  const [currentGroupIndex, setCurrentGroupIndex] = useState<number>(0); // 그룹 인덱스
  const [retryDate, setRetryDate] = useState<{ h: number; m: number; s: number }>({ h: 0, m: 0, s: 0 }); // YYYY.MM.DD HH:MM에 Retry
  const mapRef = useRef<HTMLImageElement>(null);

  const {
    data: travels,
    isLoading,
    isError,
  } = useTravelWeather({
    cCd: userInfo.hCd,
    refetchInterval: refreshTimeCount * 1000, // 30분마다 재호출
    onError: () => {
      const nowDate = new Date();
      const setDate = new Date(nowDate.getTime() + refreshTimeCount * 1000);
      setRetryDate({ h: setDate.getHours(), m: setDate.getMinutes(), s: setDate.getSeconds() }); // error 시 재요청 예정 시간 설정
    },
  });

  // 뱃지 위치 계산 최적화
  const badgePositions = useMemo(() => {
    if (!travels?.oriArr || imgW === 0) return [];

    return travels.oriArr.map((location: any) => {
      const latRecalc = Number(location.latitude) < 33.57 ? Number(location.latitude) + LAT_RANGE_CORRECTION : Number(location.latitude); // 제주도 위도일 경우 재 계산 소숫점 2자리 까지 적용
      const lonRecalc = Number(location.longitude) > 130.79 ? Number(location.longitude) - LON_RANGE_CORRECTION : Number(location.longitude); // 을릉도 경도일 경우 재 계산 소숫점 2자리 까지 적용      const latRatio = (location.latitude - LAT_RANGE.min) / (LAT_RANGE.max - LAT_RANGE.min);
      // 상대적 위치 계산
      const latRatio = (latRecalc - LAT_RANGE.min) / (LAT_RANGE.max - LAT_RANGE.min);
      const lonRatio = (lonRecalc - LON_RANGE.min) / (LON_RANGE.max - LON_RANGE.min);

      const xPos = lonRatio * imgW + imgLeft;
      const yPos = imgH * (1 - latRatio) - 5;

      return {
        ...location,
        xPos,
        yPos,
        badgePosition: { badgeX: xPos, badgeTransform: 'translateX(-50%)' },
        // badgePosition: calculateBadgePosition(xPos, imgW, imgLeft),
      };
    });
  }, [travels?.oriArr, imgW, imgH, imgLeft]);

  const renderWeatherSvg = (weather: string, isActive: boolean) => {
    // rem 단위를 픽셀 값으로 변환 (루트 폰트 크기 기준)
    const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
    const sizeSet = 6.25 * rootFontSize;
    // const colorType = isActive ? 'default' : 'dark';
    return getWeatherIcon(weather, sizeSet, { colorType: 'default', showNoWeatherIcon: true });
  };

  const weatherName = (weather: string) => {
    switch (weather) {
      case 'sunny':
        return '맑음';
      case 'cloudy':
        return '구름많음';
      case 'blur':
        return '흐림';
      case 'little_cloud':
        return '구름적음';
      case 'rain':
        return '비';
      case 'shower':
        return '소나기';
      case 'snow':
        return '눈';
      case 'sleet':
        return '진눈깨비';
      default:
        return '맑음';
    }
  };

  useEffect(() => {
    let groupInterval: any;
    let itemInterval: any;
    let locationIdx = 0;
    setCurrents([]);
    setActiveIndex(1);

    if (travels) {
      const { travelArr }: { travelArr: any[] } = travels;
      const totalNum = travelArr.reduce((acc: number, cur: any) => acc + cur.length, 0);
      setTotalLength(totalNum);
      setCurrents(travelArr[0]);

      // 각 그룹 내 아이템들을 1초마다 순차적으로 active 처리
      const startItemAnimation = (currentGroup: any[]) => {
        if (itemInterval) clearInterval(itemInterval);

        itemInterval = setInterval(() => {
          setActiveIndex((prevIndex) => {
            const nextIndex = prevIndex % currentGroup.length;
            return nextIndex + 1;
          });
        }, 5000);
      };

      // 그룹 전환 함수
      const switchToNextGroup = () => {
        locationIdx = (locationIdx + 1) % travelArr.length;
        const nextGroup = travelArr[locationIdx];

        setCurrentGroupIndex(locationIdx);
        setCurrents(nextGroup);
        setActiveIndex(1); // 새로운 그룹 시작 시 첫 번째 아이템부터 시작

        // 새로운 그룹의 아이템 애니메이션 시작
        if (nextGroup && nextGroup.length > 0) {
          startItemAnimation(nextGroup);
        }

        // 다음 그룹 전환을 위한 타이머 설정 (현재 그룹의 아이템 수 * 5초)
        if (groupInterval) clearInterval(groupInterval);
        groupInterval = setTimeout(() => {
          switchToNextGroup();
        }, nextGroup.length * 5000);
      };

      // 첫 번째 그룹의 아이템 애니메이션 시작
      if (travelArr[0] && travelArr[0].length > 0) {
        startItemAnimation(travelArr[0]);
      }

      // 출장 현장이 3개 이상일 경우에 그룹 전환 시작
      if (travelArr.length >= 2) {
        // 첫 번째 그룹의 시간만큼 대기 후 다음 그룹으로 전환
        groupInterval = setTimeout(() => {
          switchToNextGroup();
        }, travelArr[0].length * 5000);
      }
    }

    return () => {
      if (groupInterval) clearInterval(groupInterval);
      if (itemInterval) clearInterval(itemInterval);
    };
  }, [travels]);

  // 브라우저 사이즈 변경 시 이미지 사이즈 업데이트
  const updateImageSize = () => {
    if (mapRef.current) {
      setImgW(mapRef.current.clientWidth);
      setImgH(mapRef.current.clientHeight);
      setImgLeft(mapRef.current.offsetLeft);
    }
  };

  // 브라우저 로드 후 이미지 사이즈 업데이트
  const handleLoadUpdate = () => {
    updateImageSize();
  };

  // 브라우저 사이즈 변경 감지
  useEffect(() => {
    updateImageSize();

    window.addEventListener('resize', updateImageSize);
    window.addEventListener('resize', updateImageSize);

    return () => {
      window.removeEventListener('resize', updateImageSize);
    };
  }, []);

  const loading = !travels && isLoading;
  // const tempError = travels?.oriArr?.every((el: any) => el.weather.todaySky === '-');

  return (
    <Root>
      {loading ? (
        <div className='skeleton-wrap' style={{ width: '100%', height: '100%' }}>
          <SkeletonTable rows={0} mainRowHeight={390} mainRowWidth='100%' />
        </div>
      ) : (
        <>
          {/* {isError || tempError ? (
            <div className='weatherError'>
              <IssueGuide isImg={false} text={WEATHER_ERROR_MESSAGE} icon='report' />
            </div>
          ) : null} */}
          <div className='widget-body card'>
            <div className='weather_cont'>
              <div className='weather-map-img'>
                <div className='widget-headers'>
                  <div className='widget-title flex-between'>
                    <span>
                      {t(name)} <span className='badge'>{totalLength}</span>
                    </span>
                  </div>
                </div>
                <figure>
                  <img ref={mapRef} src={isTheme === '1' ? mapImage : mapImageDark} alt='대한민국 전도 이미지' onLoad={handleLoadUpdate} />
                  {badgePositions.map((location: any, idx: number) => {
                    const isActive = currentGroupIndex > 0 ? currentGroupIndex * 4 + activeIndex === idx + 1 : activeIndex === idx + 1;

                    // 뱃지 위치를 반응형으로 계산
                    const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
                    const badgeOffset = isActive ? 3.5 * rootFontSize : 3 * rootFontSize; // 3.5rem, 3rem

                    // 미리 계산된 위치 사용
                    const { xPos, yPos, badgePosition } = location;
                    const { badgeX, badgeTransform } = badgePosition;

                    const badgeTop = isActive ? yPos - 8 : yPos;

                    return (
                      <Fragment key={location.smIdx}>
                        {location.address.split(' ')[1] && (
                          <span
                            className='marker-location-badge'
                            style={{
                              display: isActive ? 'flex' : 'none',
                              left: `${badgeX}px`,
                              top: `${Number(location.latitude) > 38 ? badgeTop + 30 : badgeTop - badgeOffset}px`,
                              backgroundColor: `var(--widget-primary)`,
                              zIndex: isActive ? 9999 : 9900,
                              transform: badgeTransform,
                              whiteSpace: 'nowrap', // 한 줄로 표시
                            }}
                          >
                            {location.address.split(' ')[1]}
                          </span>
                        )}
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width={isActive ? '4rem' : '3rem'}
                          height={isActive ? '4.5rem' : '3.3rem'}
                          viewBox='0 0 34 37'
                          fill='none'
                          style={{ position: 'absolute', left: `${xPos}px`, top: `${badgeTop}px`, zIndex: isActive ? 9999 : 9900, transform: 'translate(-50%, -50%)' }}
                        >
                          <g filter={`url(#${location.smIdx})`}>
                            <path
                              d='M27 15.9474C27 20.3085 21.2768 26.2099 18.0868 29.1605C16.8982 30.2599 15.1018 30.2599 13.9132 29.1605C10.7232 26.2099 5 20.3085 5 15.9474C5 9.9013 9.92487 5 16 5C22.0751 5 27 9.9013 27 15.9474Z'
                              fill={isActive ? '#fff' : 'var(--widget-bg)'}
                            />
                          </g>
                          <path
                            d='M16 8C20.4183 8 24 11.5817 24 16C24 20.4183 16 27 16 27C16 27 8 20.4183 8 16C8 11.5817 11.5817 8 16 8ZM16 11C14.3431 11 13 11.8954 13 13C13 14.1046 14.3431 15 16 15C17.6569 15 19 14.1046 19 13C19 11.8954 17.6569 11 16 11Z'
                            fill={isActive ? 'var(--widget-primary)' : 'var(--widget-sub-text)'}
                          />
                          {isActive && (
                            <defs>
                              <filter id={location.smIdx} x='0' y='0' width='34' height='36.9854' filterUnits='userSpaceOnUse' colorInterpolationFilters='sRGB'>
                                <feFlood floodOpacity='0' result='BackgroundImageFix' />
                                <feColorMatrix in='SourceAlpha' type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0' result='hardAlpha' />
                                <feOffset dx='1' dy='1' />
                                <feGaussianBlur stdDeviation='3' />
                                <feComposite in2='hardAlpha' operator='out' />
                                <feColorMatrix type='matrix' values='0 0 0 0 0.235294 0 0 0 0 0.552941 0 0 0 0 0.968627 0 0 0 0.5 0' />
                                <feBlend mode='normal' in2='BackgroundImageFix' result='effect1_dropShadow_1542_5006' />
                                <feBlend mode='normal' in='SourceGraphic' in2='effect1_dropShadow_1542_5006' result='shape' />
                              </filter>
                            </defs>
                          )}
                        </svg>
                      </Fragment>
                    );
                  })}
                </figure>
              </div>
              {/* style={travels && travels?.length > 2 ? animationStyle : undefined} */}
              {isError ? (
                <div className='no-data'>
                  <div className='no-data-content'>
                    <span className='material-symbols-rounded'>location_off</span>
                    <p>
                      {t('출장 현장 기상 정보 조회에 실패하였습니다')} <br />
                    </p>
                  </div>

                  <div className='retry-time'>
                    <span>{t('재요청 예정 시간')}</span>
                    <p>
                      {retryDate.h.toString().padStart(2, '0')}:{retryDate.m.toString().padStart(2, '0')}:{retryDate.s.toString().padStart(2, '0')}
                    </p>
                  </div>
                </div>
              ) : currents?.length > 0 ? (
                <div className='weather-wrapper'>
                  {currents?.map((travel: any, index: number) => (
                    <div key={travel.id} className={`weather-item ${COLORS[travel.colorIdx]} ${index + 1 === activeIndex ? 'active' : ''}`}>
                      <div className='weather-left'>
                        <div className='weather-top'>{renderWeatherSvg(travel.weather.todaySky, index + 1 === activeIndex)}</div>
                        <div className='weather-bottom'>
                          {!travel.weather.errMessage && <span className='weather-temperature'>{travel.weather.todayTmp}º</span>}
                          <span className='weather-name'>{weatherName(travel.weather.todaySky)}</span>
                        </div>
                      </div>
                      <div className='weather-right'>
                        <div className='weather-head'>
                          {travel.address !== '' ? (
                            <div className='weather-address'>
                              <svg xmlns='http://www.w3.org/2000/svg' width='0.625rem' height='0.8125rem' viewBox='0 0 11 13' fill='none'>
                                <path
                                  d='M5.5 0C8.53755 0 11 2.45062 11 5.47363C11 7.57469 8.34319 10.3908 6.72266 11.9131C6.02846 12.5652 4.97154 12.5652 4.27734 11.9131C2.65681 10.3908 0 7.57469 0 5.47363C2.77065e-05 2.45062 2.46245 0 5.5 0ZM5.5 1.5C3.29086 1.5 1.5 3.29086 1.5 5.5C1.5 7.70914 5.5 11 5.5 11C5.5 11 9.5 7.70914 9.5 5.5C9.5 3.29086 7.70914 1.5 5.5 1.5ZM5.5 3C6.32843 3 7 3.44772 7 4C7 4.55228 6.32843 5 5.5 5C4.67157 5 4 4.55228 4 4C4 3.44772 4.67157 3 5.5 3Z'
                                  fill='var(--widget-text-secondary)'
                                />
                              </svg>
                              <span>
                                {travel.address.split(' ')[0].slice(0, 2)} {travel.address.split(' ')[1]} {travel.address.split(' ')[2]}
                              </span>
                            </div>
                          ) : null}
                          <h4>
                            <span>{travel.sName ?? '-'}</span>
                          </h4>
                        </div>
                        <div className='weather-etc-info'>
                          {travel.weather.errMessage ? (
                            travel.weather.errMessage
                          ) : (
                            <>
                              <div className='low'>
                                <span>최저 </span>
                                <b>{travel.weather.todayTmn}º</b>
                              </div>
                              <b> | </b>
                              <div className='high'>
                                <span>최고 </span>
                                <b>{travel.weather.todayTmx}º</b>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className='no-data'>
                  <div className='no-data-content'>
                    <span className='material-symbols-rounded'>location_off</span>
                    <p>{t('금일 출장 현장이 없습니다')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </Root>
  );
};

export default TravelWeatherInfo;
